import {
  DotsMenu,
  Icon,
  MenuOption,
  Paragraph,
  Tooltip,
} from "@smartsuite/react-ui/lib";
import { Colors, SolutionColorVariables } from "@smartsuite/types";
import { t } from "i18next";
import { observer } from "mobx-react-lite";
import type { FC } from "react";
import { useCallback, useState } from "react";
import { MenuOptionList } from "../../ActionMenu/styles";
import { useFlowCanvasMenuDismiss } from "../../FlowCanvas/hooks/use-flow-canvas-menu-dismiss";
import { findGroupByInstanceId } from "../../../stores/automation/utils/action-group-walk";
import { blockHasValidationIssue } from "../../../stores/automation/utils/inputValidation";
import { useRootStore } from "../../../stores/hooks";
import { capitalize } from "../../../locales/i18n";
import { getLocalCssColorVariables } from "../../../utils/cssVariablesUtils";
import {
  StyledBranchesBlockIcon,
  StyledBranchesBlockText,
  StyledBranchesCard,
} from "./style";
// Reuse the exact warning-icon wrapper the action cards use, so the block's
// validation triangle sits in the same spot (right: -25px, vertically centered).
import { WarningIcon } from "../../AddTriggerButton/styles";

interface BranchesBlockCardProps {
  isActive: boolean;
  /** Nested Advanced Logic (Етап 7a): renders a specific (deep) blocks group
   * instead of self-finding the first root one. Omitted for the root card,
   * which keeps its original self-find behavior for backward compatibility. */
  instanceId?: number;
  /** Stable across renders (the card binds its own instanceId) so the
   * card's React.memo isn't defeated by a fresh closure per render. */
  onSelect: (instanceId: number) => void;
  /** Called AFTER the block is deleted (immediate or confirmed), so a
   * container can fall back the active section if it lived in this block. */
  onDeleted?: () => void;
}

export const BranchesBlockCard: FC<BranchesBlockCardProps> = observer(
  ({ isActive, instanceId, onSelect, onDeleted }) => {
    const store = useRootStore();
    const candidate = store.automations.candidate;
    const [openDotMenu, setOpenDotMenu] = useState(false);
    useFlowCanvasMenuDismiss(useCallback(() => setOpenDotMenu(false), []));

    const blockGroup =
      instanceId !== undefined
        ? findGroupByInstanceId(candidate.self.actionGroups, instanceId)
        : candidate.self.actionGroups.find(
            (group) => group.config.oneofKind === "branches",
          );
    if (
      blockGroup === undefined ||
      blockGroup.config.oneofKind !== "branches"
    ) {
      return null;
    }
    const testIdSuffix =
      instanceId !== undefined ? `-${blockGroup.instanceId}` : "";

    // Only after a save attempt (submit), matching how action cards reveal
    // their warning icon -- not while the user is still building the block.
    const hasValidationError =
      candidate.isTriggerSubmitted &&
      blockHasValidationIssue(candidate, blockGroup.config.branches);

    const deleteBlock = (): void => {
      candidate.branches.deleteBlock(blockGroup.instanceId);
    };

    const onDelete = (): void => {
      if (!candidate.branches.blockHasContent(blockGroup.instanceId)) {
        deleteBlock();
        onDeleted?.();
        return;
      }
      store.modal.openConfirmModal({
        type: "warning",
        title: t("branches.delete-confirm-title"),
        description: t("branches.delete-block-confirm"),
        positiveButtonText: t("branches.delete-confirm-confirm"),
        negativeButtonText: t("branches.delete-confirm-cancel"),
        onClose: () => {
          store.modal.closeConfirmModal();
        },
        onConfirm: () => {
          deleteBlock();
          onDeleted?.();
          store.modal.closeConfirmModal();
        },
      });
    };

    return (
      <StyledBranchesCard
        data-testid={`branches-block-card${testIdSuffix}`}
        data-active={isActive}
        $isActive={isActive}
        onClick={() => onSelect(blockGroup.instanceId)}
      >
        <StyledBranchesBlockIcon>
          {/* alt-route ships only in the icon pack's MISC bucket, so
              size="MISC" is required to load it (the default "M" has no such
              icon and renders blank); dimension sets the glyph size */}
          <Icon
            name="alt-route"
            size="MISC"
            dimension={{ width: 20, height: 20 }}
            color="currentColor"
          />
        </StyledBranchesBlockIcon>
        <StyledBranchesBlockText>
          <Paragraph
            size="xxs"
            color={hasValidationError ? "error" : "secondary"}
          >
            {capitalize(t("general.action.one"))}
          </Paragraph>
          <Paragraph className="branches-block-name" weight="semibold" size="m">
            {blockGroup.config.branches.label}
          </Paragraph>
        </StyledBranchesBlockText>
        {hasValidationError && (
          <WarningIcon>
            <Tooltip
              placements={["top"]}
              text={t("branches.block-invalid")}
              target={
                <Icon
                  size="M"
                  name="important-lined"
                  color={Colors.bRed}
                  width={26}
                  height={26}
                  data-testid={`branches-block-warning${testIdSuffix}`}
                />
              }
            />
          </WarningIcon>
        )}
        <DotsMenu
          data-testid={`branches-block-menu${testIdSuffix}`}
          opened={openDotMenu}
          // stopPropagation: the toggle lives inside the clickable card, and
          // DotsMenu only preventDefault()s -- without it, merely opening the
          // kebab would bubble to the card's onClick and switch the selection
          onToggle={(open, e) => {
            e?.stopPropagation();
            setOpenDotMenu(open);
          }}
          size="s"
          minWidth={200}
          at="bottom"
          my="top+12"
          smallListPaddings={true}
          backgroundColors={{
            hover: SolutionColorVariables.sc2,
            active: SolutionColorVariables.sc2,
          }}
          colors={{
            default: Colors.dGrey,
            hover: SolutionColorVariables.sc1,
            active: SolutionColorVariables.sc1,
          }}
          portalStyles={getLocalCssColorVariables(store.theme)}
        >
          <MenuOptionList>
            <MenuOption
              key="delete-block"
              dataTestId={`branches-block-delete${testIdSuffix}`}
              onClick={(e) => {
                e.stopPropagation();
                onDelete();
                setOpenDotMenu(false);
              }}
              iconName={"trash-1"}
              iconColor={Colors.bRed}
              iconSize={"S"}
              iconSmallMargin
            >
              {t("general.delete-action")}
            </MenuOption>
          </MenuOptionList>
        </DotsMenu>
      </StyledBranchesCard>
    );
  },
);
BranchesBlockCard.displayName = "BranchesBlockCard";
